import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { DocumentTextIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import {
  DataTable,
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableRow,
  TableHeader,
} from '../components/ui/DataTable';
import DeleteResourceControl from '../components/shared/DeleteResourceControl';
import { KnowledgeStatusBadge } from '../components/knowledge/KnowledgeStatusBadge';
import { KnowledgeBaseDocumentPicker } from '../components/knowledge/KnowledgeBaseDocumentPicker';
import {
  useDeleteKnowledgeBase,
  useKnowledgeBase,
  useKnowledgeBaseDocuments,
  useRemoveKnowledgeBaseDocument,
} from '../hooks/useKnowledge';
import { useFlag } from '../hooks/useFeatureFlags';

/**
 * One knowledge base: what is in it, and what it is attached to (task 574).
 *
 * Membership only. Adding pulls from the account library; removing drops the
 * membership row and leaves the document — and its indexed chunks — exactly
 * where they were, which is why removing needs no confirmation while deleting
 * the base does.
 */
export default function KnowledgeBaseDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const documentsPageEnabled = useFlag('innovate.fe.knowledge.documents.page');

  const { data: base, isLoading, error } = useKnowledgeBase(id);
  const { data: memberships, isLoading: membershipsLoading } = useKnowledgeBaseDocuments(id);
  const removeMembership = useRemoveKnowledgeBaseDocument();
  const deleteBase = useDeleteKnowledgeBase();

  const [isPickerOpen, setIsPickerOpen] = useState(false);

  const documents = memberships ?? [];

  const handleRemove = async (documentId: string) => {
    if (!id) return;
    try {
      await removeMembership.mutateAsync({ knowledgeBaseId: id, documentId });
    } catch (removeError) {
      console.error('Failed to remove the document from the knowledge base:', removeError);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500 dark:text-gray-400">Loading knowledge base...</div>
      </div>
    );
  }

  if (error || !base) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-500 dark:text-red-400">
          Error loading knowledge base. Please try again.
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="md:flex md:items-center md:justify-between">
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{base.name}</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Knowledge base · Updated {new Date(base.updatedAt).toLocaleDateString()}
          </p>
          {base.description && (
            <p className="mt-3 text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
              {base.description}
            </p>
          )}
        </div>
        <div className="mt-4 flex md:ml-4 md:mt-0">
          {/* The one delete affordance (task 864). It keeps the testid the
              previous bespoke trigger had, so the guide and the suite that name
              it still point at the same control. */}
          <DeleteResourceControl
            flag="innovate.fe.knowledge.bases.delete"
            entity="knowledge-bases"
            entityLabel="knowledge base"
            name={base.name}
            id={base.id}
            variant="button"
            testId="knowledge-base-delete"
            body={
              <p>
                This deletes the grouping only.{' '}
                {documents.length === 1
                  ? 'The document'
                  : `All ${documents.length} documents`}{' '}
                in it stay in your library, along with any other knowledge bases they belong to.
              </p>
            }
            onDelete={() => deleteBase.mutateAsync(base.id)}
            onDeleted={() => navigate('/knowledge-bases')}
          />
        </div>
      </div>

      <div className="mt-6 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div>
            <h2 className="text-sm font-medium text-gray-900 dark:text-white">Documents</h2>
            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
              Documents live in{' '}
              {documentsPageEnabled ? (
                <Link
                  to="/knowledge-documents"
                  className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-500"
                >
                  your account library
                </Link>
              ) : (
                'your account library'
              )}
              ; this base just points at some of them.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setIsPickerOpen(true)}
            data-testid="knowledge-base-add-documents"
            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300"
          >
            <PlusIcon className="h-4 w-4 mr-1" />
            Add documents
          </button>
        </div>

        {membershipsLoading ? (
          <div className="px-6 py-8 text-sm text-gray-500 dark:text-gray-400">
            Loading documents...
          </div>
        ) : documents.length === 0 ? (
          <div className="text-center py-10">
            <DocumentTextIcon className="mx-auto h-10 w-10 text-gray-400" />
            <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
              No documents in this knowledge base yet.
            </p>
          </div>
        ) : (
          <DataTable>
            <DataTableHead>
              <TableHeader className="w-1/2">Document</TableHeader>
              <TableHeader className="w-32">Status</TableHeader>
              <TableHeader className="w-24">
                <span className="sr-only">Actions</span>
              </TableHeader>
            </DataTableHead>
            <DataTableBody>
              {documents.map((membership) => (
                <DataTableRow key={membership.id}>
                  <DataTableCell>
                    <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {membership.document?.name ?? 'Unknown document'}
                    </div>
                    {membership.document?.description && (
                      <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {membership.document.description}
                      </div>
                    )}
                  </DataTableCell>
                  <DataTableCell>
                    {membership.document ? (
                      <KnowledgeStatusBadge document={membership.document} />
                    ) : (
                      <span className="text-sm text-gray-400">—</span>
                    )}
                  </DataTableCell>
                  <DataTableCell className="whitespace-nowrap text-right text-sm font-medium">
                    <button
                      type="button"
                      onClick={() => handleRemove(membership.documentId)}
                      disabled={removeMembership.isPending}
                      aria-label={`Remove ${membership.document?.name ?? 'document'} from this knowledge base`}
                      title="Remove from this knowledge base — the document itself is kept"
                      className="text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 disabled:opacity-50"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </DataTableCell>
                </DataTableRow>
              ))}
            </DataTableBody>
          </DataTable>
        )}
      </div>

      {id && (
        <KnowledgeBaseDocumentPicker
          knowledgeBaseId={id}
          isOpen={isPickerOpen}
          onClose={() => setIsPickerOpen(false)}
        />
      )}

    </div>
  );
}
